// ── @matix/api-client ─────────────────────────────────────────────────────
//
// Grouped endpoint exports. Each group is a small object of named methods
// that wrap the shared axios instance. The instance itself is created
// per-app via createApiClient (so per-app token isolation and loader/redirect
// behavior can differ).

import type {
  CatalogCategory,
  CategoryListResponse,
  CatalogItem,
  ItemListResponse,
  FeaturedListResponse,
  InvoiceSourceListResponse,
  SearchResponse,
  SyncResponse,
  CreateOrderRequest,
  CreateOrderResponse,
  Customer,
  CustomerAddress,
  CustomerSearchResponse,
  CustomerType,
  AddressInput,
  Area,
  Governorate,
  VendorCategory,
  Country,
  Driver,
  KdsTicket,
  KdsTicketStatus,
  LoginRequest,
  LoginResponse,
  LoyaltyConfig,
  CustomerLoyalty,
  LoyaltyQuote,
  GiftCardConfig,
  GiftCardLookup,
  GiftCardOnReceipt,
  PaymentTypesResponse,
  PrintConfig,
  CreateParkedOrderRequest,
  CreateParkedOrderResponse,
  ListParkedOrdersResponse,
  GetParkedOrderResponse,
  DeleteParkedOrderResponse,
  ListDiscountReasonsResponse,
  CouponValidationResponse,
  VerifyOverrideRequest,
  VerifyOverrideResponse,
  ReceiptDetail,
  ListReceiptsRequestQuery,
  ListReceiptsResponse,
  PatchReceiptRequest,
  ReplaceReceiptRequest,
  ListHeldOrdersResponse,
  CancelHeldOrderRequest,
  PayHeldOrderRequest,
  PayHeldOrderResponse,
  UpdateHeldOrderRequest,
  UpdateHeldOrderResponse,
  CancelReason,
  VoidReceiptRequest,
  VoidReceiptResponse,
  Currency,
  GeoAddress,
  GeoConfig,
  KnetTerminalTillConfig,
  KnetTerminalTxn,
  OnlineOrderAlertsResponse,
} from '@matix/types';
import { paymentGatewayForChargeId, type PaymentGatewayCode } from '@matix/types';
import type { CustomerMeasurementsDto, CustomerMeasurementValues } from '@matix/types';
import {
  type ApiClient,
  type AxiosInstance,
  createApiClient,
  getApiErrorCode,
  getApiErrorDetails,
  getApiErrorMessage,
} from './client';

// ── Singleton: the client an app currently uses ──────────────────────────
let client: ApiClient | null = null;

export function attachClient(c: ApiClient): void {
  client = c;
}

export function getClient(): ApiClient {
  if (!client) {
    throw new Error(
      'No API client attached. Call attachClient(createApiClient(...)) at app bootstrap.',
    );
  }
  return client;
}

function http(): AxiosInstance {
  return getClient().axios;
}

// ── auth ─────────────────────────────────────────────────────────────────
export const authAPI = {
  async login(payload: LoginRequest): Promise<LoginResponse> {
    const { data } = await http().post<LoginResponse>('/api/v1/auth/login', payload);
    return data;
  },
  async me(): Promise<LoginResponse['cashier']> {
    const { data } = await http().get<LoginResponse['cashier']>('/api/v1/auth/me');
    return data;
  },
  async refresh(): Promise<{ token: string; expires_at: string }> {
    const { data } = await http().post<{ token: string; expires_at: string }>(
      '/api/v1/auth/refresh',
    );
    return data;
  },
  logout(): void {
    getClient().clearToken();
  },
};

// ── catalog ──────────────────────────────────────────────────────────────
/**
 * Item photos arrive as API-relative LINKS (`/api/v1/catalog-images/...`, see
 * pos-api itemImageLink.ts) rather than inline data URLs. The till runs on its
 * own origin, so a relative link would hit the TILL's server: prefix the API's
 * base URL. An empty base (dev — Vite proxies /api) leaves them as they are,
 * and a data URL from an older API build is left untouched.
 */
function absolutizeItemImages<T extends { image_data?: string | null }>(items: T[] | undefined): void {
  const base = (http().defaults.baseURL ?? '').replace(/\/$/, '');
  if (!base || !items) return;
  for (const it of items) {
    if (it.image_data && it.image_data.startsWith('/api/')) it.image_data = base + it.image_data;
  }
}

export const catalogAPI = {
  async listCategories(): Promise<CategoryListResponse> {
    const { data } = await http().get<CategoryListResponse>('/api/v1/catalog/categories');
    return data;
  },
  async listItemsInCategory(categoryId: number): Promise<ItemListResponse> {
    const { data } = await http().get<ItemListResponse>(
      `/api/v1/catalog/categories/${categoryId}/items`,
    );
    absolutizeItemImages(data.items);
    return data;
  },
  async getItem(id: number): Promise<CatalogItem> {
    const { data } = await http().get<CatalogItem>(`/api/v1/catalog/items/${id}`);
    absolutizeItemImages([data]);
    return data;
  },
  async search(q: string, limit = 50): Promise<SearchResponse> {
    const { data } = await http().get<SearchResponse>('/api/v1/catalog/search', {
      params: { q, limit },
    });
    absolutizeItemImages(data.results);
    return data;
  },
  async sync(since: number | string = 0): Promise<SyncResponse> {
    const { data } = await http().get<SyncResponse>('/api/v1/catalog/sync', {
      params: { since },
    });
    absolutizeItemImages(data.changed_items);
    return data;
  },
  async featured(): Promise<FeaturedListResponse> {
    const { data } = await http().get<FeaturedListResponse>('/api/v1/catalog/featured');
    absolutizeItemImages(data.items);
    return data;
  },
};

// ── invoice sources (price lists) ─────────────────────────────────────────
export const invoiceSourcesAPI = {
  /**
   * Price lists configured for this cashier's branch. Almost always empty —
   * only tenants that price the same item differently per channel have rows.
   */
  async list(): Promise<InvoiceSourceListResponse> {
    const { data } = await http().get<InvoiceSourceListResponse>('/api/v1/invoice-sources');
    return data;
  },
};

// ── customers ────────────────────────────────────────────────────────────
export const customerAPI = {
  async search(
    q: string,
    type: CustomerType = 'cash',
    limit = 20,
  ): Promise<CustomerSearchResponse> {
    const { data } = await http().get<CustomerSearchResponse>('/api/v1/customers/search', {
      params: { q, type, limit },
    });
    return data;
  },
  async getById(id: number, type: CustomerType = 'cash'): Promise<Customer> {
    const { data } = await http().get<Customer>(`/api/v1/customers/${id}`, {
      params: { type },
    });
    return data;
  },
  async createCash(input: {
    phone: string;
    name?: string;
    name_ar?: string | null;
    email?: string | null;
    category_id?: number | null;
    tax_reg_no?: string | null;
    date_of_birth?: string | null;
    gender_id?: number | null;
    /** Optional first address inserted in the same transaction. */
    address?: AddressInput;
    /** Optional list of addresses inserted in the same transaction. */
    addresses?: AddressInput[];
  }): Promise<Customer> {
    const { data } = await http().post<Customer>('/api/v1/customers/cash', input);
    return data;
  },
  /** PATCH a cash customer's scalar fields. Only provided keys are written. */
  async updateCash(
    id: number,
    input: {
      phone?: string;
      name?: string | null;
      name_ar?: string | null;
      email?: string | null;
      category_id?: number | null;
      tax_reg_no?: string | null;
      date_of_birth?: string | null;
      gender_id?: number | null;
    },
  ): Promise<Customer> {
    const { data } = await http().patch<Customer>(`/api/v1/customers/cash/${id}`, input);
    return data;
  },
  async addAddress(customerId: number, input: AddressInput): Promise<CustomerAddress> {
    const { data } = await http().post<CustomerAddress>(
      `/api/v1/customers/${customerId}/addresses`,
      input,
    );
    return data;
  },
  async updateAddress(
    customerId: number,
    addressId: number,
    input: AddressInput,
  ): Promise<CustomerAddress> {
    const { data } = await http().patch<CustomerAddress>(
      `/api/v1/customers/${customerId}/addresses/${addressId}`,
      input,
    );
    return data;
  },
  async deleteAddress(customerId: number, addressId: number): Promise<void> {
    await http().delete(`/api/v1/customers/${customerId}/addresses/${addressId}`);
  },
  /** GL_VendorCategory options for the customer form. Empty on drifted tenants. */
  async listCategories(): Promise<{ data: VendorCategory[] }> {
    const { data } = await http().get<{ data: VendorCategory[] }>('/api/v1/customers/categories');
    return data;
  },
  /**
   * mstr_Country options for the address form. Empty on drifted tenants.
   * `default_country_id` is what a NEW address pre-selects (the subsidiary's
   * own country); each row's `is_ksa` switches the form to the Saudi National
   * Address layout.
   */
  async listCountries(): Promise<{ data: Country[]; default_country_id: number | null }> {
    const { data } = await http().get<{ data: Country[]; default_country_id: number | null }>(
      '/api/v1/customers/countries',
    );
    return data;
  },
  /** GL_Governorate options — المدينة (city) on the KSA address layout. */
  async listGovernorates(countryId?: number | null): Promise<{ data: Governorate[] }> {
    const { data } = await http().get<{ data: Governorate[] }>(
      '/api/v1/customers/governorates',
      { params: countryId != null ? { country_id: countryId } : undefined },
    );
    return data;
  },
  /**
   * Add a city the tenant's lookup table is missing, from the address form.
   * Idempotent by name — an existing match comes back instead of a duplicate.
   */
  async createGovernorate(input: {
    name: string;
    name_ar?: string | null;
    country_id?: number | null;
  }): Promise<Governorate> {
    const { data } = await http().post<Governorate>('/api/v1/customers/governorates', input);
    return data;
  },
  /** Same, one level down: a district (الحي) under a city. */
  async createArea(input: {
    name: string;
    name_ar?: string | null;
    governorate_id?: number | null;
  }): Promise<Area> {
    const { data } = await http().post<Area>('/api/v1/customers/areas', input);
    return data;
  },
  async getAreaNames(ids: number[]): Promise<{ data: Area[] }> {
    if (!ids.length) return { data: [] };
    const { data } = await http().get<{ data: Area[] }>('/api/v1/customers/areas', {
      params: { ids: ids.join(',') },
    });
    return data;
  },
  /**
   * Full GL_Area list for the address-form combobox. Pass `q` for a
   * server-side LIKE filter; omit it to get all active areas (cap 500).
   * `governorateId` scopes the list to one city — the الحي list under المدينة.
   */
  async listAreas(q?: string, governorateId?: number | null): Promise<{ data: Area[] }> {
    const params: Record<string, string | number> = {};
    if (q) params.q = q;
    if (governorateId != null) params.governorate_id = governorateId;
    const { data } = await http().get<{ data: Area[] }>('/api/v1/customers/areas', {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return data;
  },
  /**
   * Customer ↔ branch visibility links (pos_customer_branches, migration 029).
   * Returns the customer's current links plus every branch in the subsidiary
   * for the picker. Empty `branch_ids` = the customer is visible everywhere.
   */
  /** Subsidiary branch options (no customer) — for the add-customer form. */
  async listBranches(): Promise<{ branches: SubsidiaryBranchOption[] }> {
    const { data } = await http().get<{ branches: SubsidiaryBranchOption[] }>(
      '/api/v1/customers/branches',
    );
    return data;
  },
  async getBranchLinks(id: number, type: CustomerType = 'cash'): Promise<CustomerBranchLinks> {
    const { data } = await http().get<CustomerBranchLinks>(`/api/v1/customers/${id}/branches`, {
      params: { type },
    });
    return data;
  },
  /**
   * Replace the customer's branch set (set-diff on the server). An empty
   * `branchIds` clears all links (visible in every branch).
   */
  async saveBranchLinks(
    id: number,
    type: CustomerType,
    branchIds: number[],
  ): Promise<CustomerBranchLinks> {
    const { data } = await http().put<CustomerBranchLinks>(`/api/v1/customers/${id}/branches`, {
      type,
      branch_ids: branchIds,
    });
    return data;
  },

  /** A customer's measurements (POS_CustomerMeasurements, migration 078). */
  async getMeasurements(type: CustomerType, id: number): Promise<CustomerMeasurementsDto> {
    const { data } = await http().get<CustomerMeasurementsDto>(`/api/v1/customers/measurements/${type}/${id}`);
    return data;
  },

  /** Replace a customer's measurements (cm). 409 MEASUREMENTS_NOT_AVAILABLE before 078. */
  async saveMeasurements(
    type: CustomerType,
    id: number,
    values: CustomerMeasurementValues,
    notes: string | null,
  ): Promise<CustomerMeasurementsDto> {
    const { data } = await http().put<CustomerMeasurementsDto>(
      `/api/v1/customers/measurements/${type}/${id}`,
      { values, notes },
    );
    return data;
  },
};

// ── geo (Google Maps address lookup) ─────────────────────────────────────
/**
 * Address lookup, proxied through pos-api so the Google key stays server-side.
 * Every method degrades quietly: a server without GOOGLE_MAPS_API_KEY answers
 * 503 and the caller just hides the feature.
 */
export const geoAPI = {
  /** Is the lookup usable here? Read once on mount; cache for the session. */
  async config(): Promise<GeoConfig> {
    const { data } = await http().get<GeoConfig>('/api/v1/geo/config');
    return data;
  },
  /**
   * Resolve a Saudi National Address short code ("RFHA6515") or free-text
   * address. `regionCode` is the ISO 3166-1 alpha-2 of the address's country
   * (Country.region_code) and restricts the search to it.
   *
   * Returns null when nothing matched — that is a normal answer, not an error.
   */
  async resolve(q: string, regionCode?: string | null): Promise<GeoAddress | null> {
    const params: Record<string, string> = { q };
    if (regionCode) params.country = regionCode;
    const { data } = await http().get<{ data: GeoAddress | null }>('/api/v1/geo/resolve', {
      params,
    });
    return data.data;
  },
  /**
   * Pinned map thumbnail as a Blob. Fetched (rather than used as an <img src>)
   * because the endpoint needs the bearer token; the caller turns it into an
   * object URL and must revoke it on unmount.
   */
  async staticMap(opts: {
    lat: number;
    lng: number;
    zoom?: number;
    w?: number;
    h?: number;
    lang?: 'en' | 'ar';
  }): Promise<Blob> {
    const { data } = await http().get<Blob>('/api/v1/geo/staticmap', {
      params: {
        lat: opts.lat,
        lng: opts.lng,
        ...(opts.zoom != null ? { zoom: opts.zoom } : {}),
        ...(opts.w != null ? { w: opts.w } : {}),
        ...(opts.h != null ? { h: opts.h } : {}),
        ...(opts.lang ? { lang: opts.lang } : {}),
      },
      responseType: 'blob',
    });
    return data;
  },
};

/** One branch option in the customer-branch link picker. */
export interface SubsidiaryBranchOption {
  id: number;
  code: string;
  name: string;
  name_ar: string | null;
}

/** GET/PUT /customers/:id/branches response. */
export interface CustomerBranchLinks {
  type: CustomerType;
  customer_id: number;
  branch_ids: number[];
  branches: SubsidiaryBranchOption[];
}

// ── drivers ──────────────────────────────────────────────────────────────
export const driverAPI = {
  async list(): Promise<{ data: Driver[] }> {
    const { data } = await http().get<{ data: Driver[] }>('/api/v1/drivers');
    return data;
  },
};

// ── orders ───────────────────────────────────────────────────────────────
export const orderAPI = {
  /**
   * `timeoutMs` exists for the till's offline queue.
   *
   * The shared 30 s default is far too long to leave a cashier watching a
   * "Saving…" button with a customer in front of them — and it is exactly what
   * happens when the connection dies AFTER the request goes out: the server
   * writes the order, the response never arrives, and the client sits out the
   * whole timeout. Giving up sooner is safe here and nowhere else, because
   * this endpoint dedupes on `idempotency_key` against a unique index: a sale
   * that did land is returned as `idempotent_replay` when the queue re-sends
   * it, never written twice.
   */
  async create(
    payload: CreateOrderRequest,
    opts: { timeoutMs?: number } = {},
  ): Promise<CreateOrderResponse> {
    const { data } = await http().post<CreateOrderResponse>('/api/v1/orders', payload, {
      ...(opts.timeoutMs != null ? { timeout: opts.timeoutMs } : {}),
    });
    return data;
  },
};

// ── payments ─────────────────────────────────────────────────────────────
export const paymentAPI = {
  async listTypes(): Promise<PaymentTypesResponse> {
    const { data } = await http().get<PaymentTypesResponse>('/api/v1/payment-types');
    return data;
  },
};

// ── KNET card terminal at the till (KNET_TERMINAL.md) ───────────────────
//
// The terminal is driven by eSocket.POS through the shop's SCPS agent; the
// till only queues an attempt, polls it, and puts the approved attempt's id on
// the payment slice it pays.

export const knetTerminalAPI = {
  async config(): Promise<KnetTerminalTillConfig> {
    const { data } = await http().get<KnetTerminalTillConfig>('/api/v1/knet-terminal/config');
    return data;
  },
  async purchase(body: {
    amount: number;
    payment_type_code: number;
    order_idempotency_key?: string | null;
  }): Promise<KnetTerminalTxn> {
    const { data } = await http().post<{ txn: KnetTerminalTxn }>('/api/v1/knet-terminal/purchase', body);
    return data.txn;
  },
  async refund(body: {
    amount: number;
    payment_type_code: number;
    parent_receipt_id: number;
    order_idempotency_key?: string | null;
  }): Promise<KnetTerminalTxn> {
    const { data } = await http().post<{ txn: KnetTerminalTxn }>('/api/v1/knet-terminal/refund', body);
    return data.txn;
  },
  async get(id: number): Promise<KnetTerminalTxn> {
    const { data } = await http().get<{ txn: KnetTerminalTxn }>(`/api/v1/knet-terminal/txns/${id}`);
    return data.txn;
  },
  async cancel(id: number): Promise<KnetTerminalTxn> {
    const { data } = await http().post<{ txn: KnetTerminalTxn }>(`/api/v1/knet-terminal/txns/${id}/cancel`, {});
    return data.txn;
  },
  async reverse(id: number): Promise<KnetTerminalTxn> {
    const { data } = await http().post<{ txn: KnetTerminalTxn }>(`/api/v1/knet-terminal/txns/${id}/reverse`, {});
    return data.txn;
  },
};

// ── Tap payment gateway (Inv_PaymentType.Code = -3) ──────────────────────
//
// Two-step flow:
//   1. createCharge  — POSTs the full order draft; backend opens a Tap
//                      charge but does NOT post the GL voucher yet.
//   2. pollCharge    — Cashier UI polls every 3s; on first CAPTURED the
//                      backend runs createCashOrder atomically and returns
//                      the order details in `order`.
//
// The same `idempotency_key` used for the order is reused as the Tap
// reference so duplicate POSTs from the cashier UI return the same charge.

export type TapChargeStatus = 'pending' | 'captured' | 'failed' | 'cancelled';
export type TapTerminalMode = 'qr' | 'iframe';

export interface CreateTapChargeRequest {
  order: CreateOrderRequest;
  customer?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone_country_code?: string;
    phone_number?: string;
  };
  return_url?: string;
}

export interface CreateTapChargeResponse {
  charge_id: string;
  status: TapChargeStatus;
  tap_status: string | null;
  amount: number;
  currency: string;
  redirect_url: string | null;
  terminal_mode: TapTerminalMode;
}

export interface TapChargeStatusResponse {
  charge_id: string;
  status: TapChargeStatus;
  tap_status: string | null;
  amount: number;
  currency: string;
  order?: CreateOrderResponse;
  failure_reason: string | null;
}

export const tapPaymentAPI = {
  async createCharge(payload: CreateTapChargeRequest): Promise<CreateTapChargeResponse> {
    const { data } = await http().post<CreateTapChargeResponse>(
      '/api/v1/payments/tap/charges',
      payload,
    );
    return data;
  },

  async pollCharge(chargeId: string): Promise<TapChargeStatusResponse> {
    const { data } = await http().get<TapChargeStatusResponse>(
      `/api/v1/payments/tap/charges/${encodeURIComponent(chargeId)}`,
    );
    return data;
  },

  async cancelCharge(chargeId: string): Promise<{ cancelled: boolean }> {
    const { data } = await http().post<{ cancelled: boolean }>(
      `/api/v1/payments/tap/charges/${encodeURIComponent(chargeId)}/cancel`,
    );
    return data;
  },
};

// ── Hosted payment gateways (Tap, UPayments, Tabby, Tamara, MyFatoorah) ───
//
// The same two-step flow as Tap's, for every gateway, on
// /api/v1/payments/gateways/:gateway/… . The gateway is chosen when the charge
// is opened; the charge id that comes back carries it, so polling and
// cancelling take only the id. `line_items` are the basket lines, named —
// the buy-now-pay-later providers price the basket line by line.

export interface CreateGatewayChargeRequest extends CreateTapChargeRequest {
  line_items?: Array<{ name: string; qty: number; unit_price: number; sku?: string }>;
}

export interface CreateGatewayChargeResponse extends CreateTapChargeResponse {
  gateway: PaymentGatewayCode;
  idempotent_replay?: boolean;
}

export interface GatewayChargeStatusResponse extends TapChargeStatusResponse {
  gateway: PaymentGatewayCode;
}

export interface AvailableGateway {
  gateway: PaymentGatewayCode;
  payment_type_code: number;
  name: string;
  name_ar: string;
  requires_phone: boolean;
  is_bnpl: boolean;
}

export const gatewayPaymentAPI = {
  async createCharge(
    gateway: PaymentGatewayCode,
    payload: CreateGatewayChargeRequest,
  ): Promise<CreateGatewayChargeResponse> {
    const { data } = await http().post<CreateGatewayChargeResponse>(
      `/api/v1/payments/gateways/${encodeURIComponent(gateway)}/charges`,
      payload,
    );
    return data;
  },

  async pollCharge(chargeId: string): Promise<GatewayChargeStatusResponse> {
    const gateway = paymentGatewayForChargeId(chargeId);
    const { data } = await http().get<GatewayChargeStatusResponse>(
      `/api/v1/payments/gateways/${encodeURIComponent(gateway)}/charges/${encodeURIComponent(chargeId)}`,
    );
    return data;
  },

  async cancelCharge(chargeId: string): Promise<{ cancelled: boolean }> {
    const gateway = paymentGatewayForChargeId(chargeId);
    const { data } = await http().post<{ cancelled: boolean }>(
      `/api/v1/payments/gateways/${encodeURIComponent(gateway)}/charges/${encodeURIComponent(chargeId)}/cancel`,
    );
    return data;
  },

  /** The gateways enabled for this company right now. */
  async available(): Promise<{ gateways: AvailableGateway[] }> {
    const { data } = await http().get<{ gateways: AvailableGateway[] }>(
      '/api/v1/payments/gateways/available',
    );
    return data;
  },
};

// ── tenant: print config (Phase 5) ──────────────────────────────────────
export const printConfigAPI = {
  async get(): Promise<PrintConfig> {
    const { data } = await http().get<{ data: PrintConfig }>('/api/v1/tenant/print-config');
    return data.data;
  },
};

// ── branch: tables (read-only for cashier) ───────────────────────────────
//
// Surfaces BranchTables + mstr_Floor for the cashier's branch. The
// branch_id is derived from the JWT — no parameter needed. Used by the
// TablePickerModal so the cashier can attach a table to the in-progress
// order.
export interface BranchFloorDto {
  id: number;
  name: string;
  name_ar: string | null;
  branch_id: number;
  tables_count: number;
}

export interface BranchTableDto {
  id: number;
  branch_id: number;
  floor_id: number | null;
  floor_name: string | null;
  table_number: number;
  name: string | null;
  qr_token: string;
  seats_count: number | null;
  is_reserved: boolean;
  has_open_receipt: boolean;
  /** The HELD order on this table (IsHolded = 1) — taken until paid or cancelled. */
  held_receipt_id?: number | null;
  held_entry_no?: number | null;
  /** Every held order on the table, newest first. Empty/absent = free. */
  held_orders?: Array<{
    receipt_id: number;
    entry_no: number;
    employee_id: number | null;
    employee_name: string | null;
    net_value: number;
    entry_date: string;
    /** Who has this order open in a till right now; null = nobody. */
    open_by?: { user_id: number; user_name: string | null; at: string } | null;
  }>;
}

export const branchTablesAPI = {
  async listFloors(): Promise<BranchFloorDto[]> {
    const { data } = await http().get<{ data: BranchFloorDto[] }>('/api/v1/branch/floors');
    return data.data;
  },
  async listTables(floorId?: number | null): Promise<BranchTableDto[]> {
    const params = floorId != null ? { floorId } : undefined;
    const { data } = await http().get<{ data: BranchTableDto[] }>('/api/v1/branch/tables', { params });
    return data.data;
  },
};

// ── tenant: tax config ───────────────────────────────────────────────────
//
// Source-of-truth VAT settings for the cashier's subsidiary. Loaded once
// on app boot so the cart can compute totals using the real per-tenant
// rate instead of a hardcoded constant.

export interface TaxConfigDto {
  /** Percentage as a whole number (15 means 15%). 0 = no tax applied. */
  sales_percentage: number;
  /** True when the displayed item price already includes VAT. */
  price_inclusive: boolean;
  label_en: string;
  label_ar: string;
}

export const taxConfigAPI = {
  async get(): Promise<TaxConfigDto> {
    const { data } = await http().get<{ data: TaxConfigDto }>('/api/v1/tenant/tax-config');
    return data.data;
  },
};

// ── tenant: order settings ───────────────────────────────────────────────
//
// The default order type a fresh sale opens on (set by the admin). Loaded
// once on app boot so the cashier lands on the tenant's usual order mode.

export interface OrderSettingsDto {
  /** 'takeaway' | 'dine-in' | 'delivery', or null when the admin hasn't set one. */
  default_order_type: string | null;
}

export const orderSettingsAPI = {
  async get(): Promise<OrderSettingsDto> {
    const { data } = await http().get<{ data: OrderSettingsDto }>('/api/v1/tenant/order-settings');
    return data.data;
  },
};

// ── tenant: POS feature flags ────────────────────────────────────────────
//
// Legacy mstr_ConfigurationParameter flags surfaced to the cashier. Loaded
// once on boot to decide whether to show the dine-in/tables flow and per-item
// product photos.

export interface TenantConfigDto {
  /** false → hide the dine-in / tables flow on the cashier. */
  is_restaurant: boolean;
  /** true → show per-item photos in the catalog when available. */
  is_show_product_image: boolean;
  /** Order types — show the matching order-mode pill when true. */
  is_takeaway: boolean;
  is_delivery: boolean;
  is_pickup: boolean;
  /** Dine-in (tables) pill — also requires is_restaurant. */
  is_dine_in: boolean;
  is_membership: boolean;
  /** true → allow editing a line's total price. */
  is_total_price_changes: boolean;
  /** When editing the total, adjust the quantity or the unit price. */
  when_change_total: 'quantity' | 'price';
  /** false → the per-line salesman selects are hidden entirely. */
  is_salesman_implemented: boolean;
  /** true → a salesman is required on every (non-membership) cart line. */
  is_salesman_mandatory: boolean;
  /** true → offer a second salesman per line. */
  activate_second_salesman: boolean;
  /** true → print a per-salesman item summary after the customer receipt. */
  print_salesman_summary: boolean;
  /** true → match the whole name/code/barcode, not a substring. */
  full_barcode_scan: boolean;
  /** true → a repeat scan bumps the existing cart line. */
  combine_qty_on_scan: boolean;
  /** false → hide parking (hold) an order. */
  is_hold_implemented: boolean;
  /**
   * How the "back to stock" box starts on a return line. Optional: an API
   * older than this build omits it, which means ticked.
   */
  restock_on_return_default?: boolean;
  /** true → print the shift serial on the Z-report. */
  print_shift_serial: boolean;
  /** true → an order cannot be charged without a customer attached. */
  is_customer_mandatory: boolean;
  /**
   * POS_CreditRequiresCreditCustomer — Credit (Code 2) only alone and for a
   * credit customer. Optional: an API older than this build omits it (off).
   */
  credit_requires_credit_customer?: boolean;
  /** true → a customer cannot be saved without a category. */
  customer_category_is_mandatory: boolean;
  /** true → reject a phone number another customer already has. */
  block_duplicate_customer_phone: boolean;
  /** true → show the customer↔branch linkage UI (admin screen + cashier popup). */
  customer_branch_link: boolean;
  /** POS_CustomerMeasurements — show the customer measurements. Optional: older APIs omit it (off). */
  customer_measurements?: boolean;
  /** true → the Appointments module is enabled for this tenant (cashier shows the link). */
  appointments_enabled: boolean;
  /**
   * Business-profile modules the setup wizard switched on. null / absent =
   * every module (every tenant onboarded before the wizard, or an older API).
   */
  enabled_modules?: string[] | null;
  /**
   * Master gate for taking a sale with no network. Optional on the wire so a
   * cashier build newer than the API still parses; readers must treat a
   * missing value as FALSE, since the safe default is "do not queue".
   */
  offline_sales_enabled?: boolean;
  /**
   * false → the tenant blocks selling below stock (mstr_InventroySettings.
   * PhysicalNegativeInventoryID): the server refuses a short basket with
   * INSUFFICIENT_STOCK and the till warns as the cart is built. Optional on
   * the wire; a missing value means ALLOWED, the ERP's own default.
   */
  allow_negative_stock?: boolean;
  /**
   * Embedded-barcode ("scale barcode") layout: the till splits a scanned label
   * of exactly `total_digits` characters into an item code and a quantity.
   * `enabled` is the legacy `Barcodescannersetting` flag; the digits come from
   * `PosBarcodeScanerConfiguration`. Optional on the wire so a cashier build
   * newer than the API still parses — a missing value means the feature is off,
   * which is what every tenant but AlSaqi is.
   */
  barcode_scanner?: {
    enabled: boolean;
    total_digits: number | null;
    item_code_digits: number | null;
    item_code_start: number | null;
    qty_digits: number | null;
    qty_start: number | null;
    qty_whole_digits: number | null;
  };
}

// ── tenant base currency ──────────────────────────────────────────────────
//
// `GET /api/v1/tenant/currency` → the subsidiary's base currency, including
// its PRECISION (3 on KWD/BHD/OMR, 2 on EGP/SAR/AED). The cashier reads the
// same value off PrintConfig (one less round-trip); this endpoint is for the
// surfaces that never load a print config — the appointments app, standalone
// screens — but still have to render and round money the tenant's way.
export const currencyAPI = {
  async get(): Promise<Currency> {
    const { data } = await http().get<{ data: Currency }>('/api/v1/tenant/currency');
    return data.data;
  },
};

export const tenantConfigAPI = {
  async get(): Promise<TenantConfigDto> {
    const { data } = await http().get<{ data: TenantConfigDto }>('/api/v1/tenant/config');
    return data.data;
  },
};

// ── salesmen (per-line salesman dropdowns) ────────────────────────────────
export interface SalesmanLookup { id: number; name: string; name_ar: string | null }

export const salesmenAPI = {
  async list(): Promise<SalesmanLookup[]> {
    const { data } = await http().get<{ salesmen: SalesmanLookup[] }>('/api/v1/salesmen');
    return data.salesmen;
  },
  /**
   * "Change Salesman" — who a receipt may be credited to: the branch's
   * Cashier + Salesman users. `can_change` false = the button is not offered.
   */
  async creators(): Promise<{ can_change: boolean; users: SalesmanLookup[] }> {
    const { data } = await http().get<{ can_change: boolean; users: SalesmanLookup[] }>(
      '/api/v1/salesmen/creators',
    );
    return data;
  },
};

//
// Membership (prepaid package) — a customer's live packages and their
// remaining redeemable items, plus expiry maintenance.

export interface MembershipItemDto {
  detail_id: number;
  item_id: number;
  unit_id: number | null;
  item_name: string | null;
  item_name_ar: string | null;
  unit_name: string | null;
  unit_name_ar: string | null;
  purchased_qty: number;
  redeemed_qty: number;
  remaining_qty: number;
}

export interface MembershipDto {
  membership_id: number;
  customer_id: number;
  customer_name: string | null;
  customer_name_ar: string | null;
  creation_date: string;
  expiration_date: string;
  items: MembershipItemDto[];
}

export const membershipAPI = {
  /** Live packages + remaining items for a cash customer. */
  async forCustomer(customerId: number): Promise<MembershipDto[]> {
    const { data } = await http().get<{ data: MembershipDto[] }>(
      `/api/v1/membership/customer/${customerId}`,
    );
    return data.data;
  },
  /** Move a package's expiry date (ISO 8601). */
  async updateExpiration(membershipId: number, expirationDateIso: string): Promise<void> {
    await http().put(`/api/v1/membership/${membershipId}/expiration`, {
      expiration_date: expirationDateIso,
    });
  },
};

// ── loyalty ──────────────────────────────────────────────────────────────
//
// Read-only from the cashier's side: points only ever move as part of an
// order transaction (or a back-office adjustment), never through a call the
// till makes on its own. `quote` is a preview — the server re-validates the
// redemption under lock when the order is charged.
export const loyaltyAPI = {
  /** The programme + whether the caller's branch takes part. */
  async config(): Promise<LoyaltyConfig> {
    const { data } = await http().get<{ data: LoyaltyConfig }>('/api/v1/loyalty/config');
    return data.data;
  },
  /** Balance, expiry nudge and recent statement for one customer. */
  async forCustomer(customerId: number): Promise<CustomerLoyalty> {
    const { data } = await http().get<{ data: CustomerLoyalty }>(
      `/api/v1/loyalty/customer/${customerId}`,
    );
    return data.data;
  },
  /**
   * Price a redemption before charging. `redeemable_base` is the order's
   * pre-tax base (sale lines − cashier discount + delivery) so the percentage
   * cap applies to the basket in front of the cashier.
   */
  async quote(payload: {
    customer_id: number;
    requested_points: number;
    redeemable_base: number;
  }): Promise<LoyaltyQuote> {
    const { data } = await http().post<{ data: LoyaltyQuote }>('/api/v1/loyalty/quote', payload);
    return data.data;
  },
};

// ── gift cards ───────────────────────────────────────────────────────────
//
// Read-only from the cashier's side: a balance only ever moves as part of an
// order transaction (or a back-office adjustment), never through a call the till
// makes on its own. `lookup` is a preview — the server re-validates the card
// under lock when the order is charged.
export const giftCardsAPI = {
  /** The tenant's setup: is it on, which tender, which item, which values. */
  async config(): Promise<GiftCardConfig> {
    const { data } = await http().get<{ data: GiftCardConfig }>('/api/v1/gift-cards/config');
    return data.data;
  },
  /**
   * Validate a card and read its balance. Pass `amount` to ask the sharper
   * question ("can it cover this?") rather than only ("is it any good?").
   *
   * Throttled server-side per cashier: a card number is a bearer token for
   * money, so this endpoint cannot be walked to enumerate numbers.
   */
  async lookup(payload: {
    card_number: string;
    pin?: string | null;
    amount?: number | null;
  }): Promise<GiftCardLookup> {
    const { data } = await http().post<{ data: GiftCardLookup }>(
      '/api/v1/gift-cards/lookup',
      payload,
    );
    return data.data;
  },
  /**
   * Which cards paid for a receipt, and how much each may still get back.
   * Drives refund-to-card: the cashier picks the card that funded the invoice
   * instead of typing a number.
   */
  async forReceipt(receiptId: number): Promise<GiftCardOnReceipt[]> {
    const { data } = await http().get<{ data: GiftCardOnReceipt[] }>(
      `/api/v1/gift-cards/for-receipt/${receiptId}`,
    );
    return data.data;
  },
};

// ── parked orders (Phase 6) ──────────────────────────────────────────────
export const parkedOrdersAPI = {
  async create(payload: CreateParkedOrderRequest): Promise<CreateParkedOrderResponse> {
    const { data } = await http().post<CreateParkedOrderResponse>(
      '/api/v1/parked-orders',
      payload,
    );
    return data;
  },
  async list(): Promise<ListParkedOrdersResponse> {
    const { data } = await http().get<ListParkedOrdersResponse>('/api/v1/parked-orders');
    return data;
  },
  async getById(id: string): Promise<GetParkedOrderResponse> {
    const { data } = await http().get<GetParkedOrderResponse>(
      `/api/v1/parked-orders/${encodeURIComponent(id)}`,
    );
    return data;
  },
  async deleteById(id: string): Promise<DeleteParkedOrderResponse> {
    const { data } = await http().delete<DeleteParkedOrderResponse>(
      `/api/v1/parked-orders/${encodeURIComponent(id)}`,
    );
    return data;
  },
};

// ── discounts & coupons (Phase 8) ────────────────────────────────────────
//
// 3 endpoints:
//   - listReasons       → dropdown options for the modal
//   - validateCoupon    → server-side coupon check + amount preview
//   - verifyOverride    → manager username/password to authorize over-limit
export const discountsAPI = {
  async listReasons(): Promise<ListDiscountReasonsResponse> {
    const { data } = await http().get<ListDiscountReasonsResponse>('/api/v1/discount-reasons');
    return data;
  },
  async validateCoupon(args: {
    code: string;
    subtotal: number;
    customerId: number | null;
  }): Promise<CouponValidationResponse> {
    const params: Record<string, string> = {
      code: args.code,
      subtotal: args.subtotal.toString(),
    };
    if (args.customerId !== null) {
      params['customer_id'] = String(args.customerId);
    }
    const { data } = await http().get<CouponValidationResponse>('/api/v1/coupons/validate', {
      params,
    });
    return data;
  },
  async verifyOverride(payload: VerifyOverrideRequest): Promise<VerifyOverrideResponse> {
    const { data } = await http().post<VerifyOverrideResponse>(
      '/api/v1/auth/verify-override',
      payload,
    );
    return data;
  },
};

// ── coupon engine (migration 038) ────────────────────────────────────────
//
// The NEW coupon engine, separate from the legacy `discountsAPI.validateCoupon`
// above (which reads GL_Coupon_* and is left untouched). Note the verb: the
// legacy validator owns GET /coupons/validate, this one POSTs because it sends
// the basket — the server prices the discount from the actual lines.
//
// `reason: 'COUPONS_NOT_AVAILABLE'` means this tenant has not had migration 038
// applied; the caller falls back to the legacy validator so nothing regresses.

/** One basket line, as the engine needs it. */
export interface CouponBasketLineInput {
  item_id: number;
  qty: number;
  /** Line total AFTER any line-level discount. */
  total: number;
}

export interface CouponEngineResult {
  valid: boolean;
  reason: string | null;
  message: string | null;
  coupon_id: number | null;
  code: string | null;
  name: string | null;
  discount_type: 'percentage' | 'fixed' | 'free_item' | 'free_delivery' | null;
  /** Server-priced money off the order. Never trust a client-side figure. */
  discount_amount: number;
  eligible_total: number;
}

export const couponsAPI = {
  async validate(args: {
    code: string;
    customerId?: number | null;
    orderType?: number | null;
    deliveryFee?: number;
    lines: CouponBasketLineInput[];
  }): Promise<CouponEngineResult> {
    const { data } = await http().post<CouponEngineResult>('/api/v1/coupons/validate', {
      code: args.code,
      ...(args.customerId != null ? { customer_id: args.customerId } : {}),
      ...(args.orderType != null ? { order_type: args.orderType } : {}),
      ...(args.deliveryFee ? { delivery_fee: args.deliveryFee } : {}),
      lines: args.lines,
    });
    return data;
  },
};

// ── shifts (Phase 10 — cash drawer closing) ──────────────────────────────
//
// Endpoints, all under /api/v1/shifts:
//   GET  /current             → current open shift, 204 if none
//   GET  /current/summary     → close-shift summary (totals + receipts + movements)
//   POST /current/close       → close + emit Z-Report
//   GET  /:id/z-report        → frozen Z-Report snapshot
//
// Types are local to this file to avoid a cross-package dance with @matix/types
// for v1; once these stabilize they should move to @matix/types alongside
// the other DTOs.

export interface CashShiftDto {
  id: number;
  drawer_id: number;
  drawer_name: string | null;
  branch_id: number;
  opened_at: string;
  opening_float: number;
  status: 'open' | 'closed';
  estimated_drawer_cash: number;
}

export interface ShiftPaymentBucket {
  count: number;
  total: number;
}

export interface ShiftSummaryDto {
  shift: CashShiftDto;
  expected_breakdown: {
    opening_float: number;
    cash_sales: number;
    cash_refunds: number;
    pickups: number;
    drops: number;
    expenses: number;
    /** Cash expense vouchers paid from the drawer — already off expected_total. Absent on older APIs. */
    expense_vouchers_cash?: number;
    expected_total: number;
  };
  /**
   * Revenue view of the shift. total_sales = sales − returns;
   * net_sales = total_sales − discounts + tax + delivery_fees.
   */
  sales_summary: {
    sales: number;
    returns: number;
    total_sales: number;
    discounts: number;
    tax: number;
    delivery_fees: number;
    net_sales: number;
  };
  payment_totals: {
    cash: ShiftPaymentBucket;
    card: ShiftPaymentBucket;
    wallet: ShiftPaymentBucket;
    credit: ShiftPaymentBucket;
    other: ShiftPaymentBucket;
  };
  receipts_by_type: ShiftReceiptGroupDto[];
  receipts_by_payment: ShiftReceiptGroupDto[];
  movements: CashMovementDto[];
  /**
   * The card / network count (migration 071): the shift's slip-backed tenders
   * and what each took. Absent on an older API; `available` false without 071.
   */
  tender_count?: { available: boolean; tenders: TenderExpectationDto[] };
}

/** A card-family tender of a shift (Code >= 3, not the gift card) and its takings. */
export interface TenderExpectationDto {
  payment_type_id: number;
  payment_code: number;
  label: string;
  label_ar: string | null;
  receipt_count: number;
  expected: number;
}
/** A tender counted at close: counted − expected = variance. */
export interface TenderCountDto extends TenderExpectationDto {
  counted: number;
  variance: number;
}

export interface ShiftReceiptGroupDto {
  key: number | string;
  label: string;
  count: number;
  total: number;
  receipts: ShiftReceiptRowDto[];
  order_type?: number | null;
  order_type_label?: string;
  payment_type?: number | null;
  payment_label?: string;
}

export interface ShiftReceiptRowDto {
  id: number;
  code: string;
  time: string;
  /** Signed: negative for returns. */
  total: number;
  payment_type: number | null;
  payment_label: string | null;
  is_canceled: boolean;
  is_return: boolean;
}

export interface CloseShiftRequestDto {
  /** 'single' skips the per-denomination breakdown entirely. */
  count_mode: 'denom' | 'single';
  /** Required and sum-checked only when count_mode is 'denom'. */
  denominations?: Array<{ denomination: number; count: number }>;
  counted_total: number;
  notes?: string;
  /** What the card terminal / slips say per tender; a tender left out is not counted. */
  tender_counts?: Array<{ payment_type_id: number; counted: number }>;
}

export interface CloseShiftResponseDto {
  shift_id: number;
  expected_total: number;
  counted_total: number;
  variance: number;
  z_report_id: number;
  components_inserted: {
    attempted: number;
    succeeded: number;
    failed: number;
    errors: Array<{ receipt_id: number; error: string }>;
  };
}

/** One row of a Z-report breakdown: how many orders, and how much. */
export interface ZReportBreakdownGroupDto {
  key: string;
  label: string;
  /** Arabic name, where the group has one (payment types). */
  label_ar?: string | null;
  count: number;
  /** Signed net — a returns row is negative. */
  total: number;
}

export interface ZReportBreakdownsDto {
  by_order_type: ZReportBreakdownGroupDto[];
  /** By the receipt HEADER's payment type; a split lands under "Multi". */
  by_payment_type: ZReportBreakdownGroupDto[];
  /** MONEY by payment method under the tenant's own names (split-aware). Absent on an older API. */
  by_payment_method?: ZReportBreakdownGroupDto[];
  composition: ZReportBreakdownGroupDto[];
  /** `count` is DISTINCT receipts the salesman appears on, not lines. */
  by_salesman: ZReportBreakdownGroupDto[];
}

export interface ZReportDto {
  id: number;
  shift_id: number;
  generated_at: string;
  printed_count: number;
  last_printed_at: string | null;
  header: {
    branch_name: string | null;
    cashier_name: string | null;
    drawer_name: string | null;
    opened_at: string;
    closed_at: string | null;
    shift_code: string;
  };
  sales: {
    cash: number; card: number; wallet: number; credit: number; other: number;
    refunds_cash: number; discounts_total: number; vat_total: number;
    gross_sales: number; net_sales: number;
  };
  receipts: {
    receipt_count: number; voided_count: number; avg_ticket: number | null;
  };
  /**
   * The document-number span the shift covered — "invoices 40310 to 40312".
   * One entry per receipt code, never merged: each code runs its own Entry_No
   * sequence. Optional so an older API build simply prints no such block.
   */
  document_ranges?: Array<{
    code: number;
    count: number;
    cancelled_count: number;
    first_no: number | null;
    last_no: number | null;
  }>;
  /**
   * Counts behind the money — by order type, by payment type, sales vs
   * returns vs discounted, and by salesman. Recomputed server-side from the
   * shift's receipts on each read (the snapshot holds no breakdown), so it
   * works on historic shifts too. Null when that recount failed.
   */
  breakdowns: ZReportBreakdownsDto | null;
  cash_drawer: {
    opening_float: number; pickups_total: number; drops_total: number;
    expenses_total: number; expected_cash: number; counted_cash: number;
    variance: number;
  };
  denominations: Array<{ denomination: number; count: number; subtotal: number }>;
  adjustments: Array<{
    receipt_id: number; receipt_code: string | null;
    old_label: string | null; new_label: string | null;
    changed_by_name: string | null; approved_by_name: string | null;
    changed_at: string; reason: string | null;
  }>;
  /** The card / network count taken at close (empty / absent when none). */
  tender_counts?: TenderCountDto[];
}

export interface MyShiftListItemDto {
  id: number;
  status: 'open' | 'closed';
  /** Z-NNNN once closed (the Z-report id, matching the printed slip); null while open. */
  shift_code: string | null;
  branch_id: number;
  branch_name: string | null;
  drawer_id: number;
  drawer_name: string | null;
  opened_at: string;
  closed_at: string | null;
  opening_float: number;
  expected_total: number | null;
  counted_total: number | null;
  variance: number | null;
  receipt_count: number;
  receipts_net: number;
  /** True only once the close wrote a Z-report snapshot — the print is offered on this. */
  has_z_report: boolean;
}
export interface MyShiftListDto {
  data: MyShiftListItemDto[];
  page: number;
  limit: number;
  total: number;
}
export interface MyShiftListQuery {
  from?: string;
  to?: string;
  /** `HH:mm` on each bound, as the reports take; omitted = the whole day. */
  from_time?: string;
  to_time?: string;
  page?: number;
  limit?: number;
}

export const shiftAPI = {
  /** The authenticated cashier's own shifts, newest first — Reports → My Shifts. */
  async list(query: MyShiftListQuery = {}): Promise<MyShiftListDto> {
    const { data } = await http().get<MyShiftListDto>('/api/v1/shifts', { params: query });
    return data;
  },
  async current(): Promise<CashShiftDto | null> {
    const { data, status } = await http().get<CashShiftDto>('/api/v1/shifts/current', {
      validateStatus: (s) => s === 200 || s === 204,
    });
    return status === 204 ? null : data;
  },
  async summary(): Promise<ShiftSummaryDto | null> {
    const { data, status } = await http().get<ShiftSummaryDto>('/api/v1/shifts/current/summary', {
      validateStatus: (s) => s === 200 || s === 404,
    });
    return status === 404 ? null : data;
  },
  async close(payload: CloseShiftRequestDto): Promise<CloseShiftResponseDto> {
    const { data } = await http().post<CloseShiftResponseDto>('/api/v1/shifts/current/close', payload);
    return data;
  },
  async zReport(shiftId: number): Promise<ZReportDto> {
    const { data } = await http().get<ZReportDto>(`/api/v1/shifts/${shiftId}/z-report`);
    return data;
  },
};

// ── reports ───────────────────────────────────────────────────────────────

export interface ReportLookupItem { id: number; name: string; name_ar: string | null }
export interface ReportFiltersDto {
  branches: ReportLookupItem[];
  cashiers: ReportLookupItem[];
  categories: ReportLookupItem[];
  salesmen: ReportLookupItem[];
  /** Sales summary's cashier filter: Cashier-group users with the branch. Optional: older APIs omit it. */
  sales_cashiers?: ReportLookupItem[];
  default_branch_id: number | null;
}
export interface QuickSalesRow { metric: string; metric_ar: string | null; count: number; amount: number }
export interface QuickSalesDto {
  payment_types: QuickSalesRow[];
  order_types: QuickSalesRow[];
  total_sales: { count: number; amount: number };
  total_returns: { count: number; amount: number };
  net_sales: number;
}
export interface BizGroupRow { metric: string; metric_ar: string | null; qty: number; value: number; qty_pct: number; value_pct: number }
export interface BusinessSummaryDto {
  revenues: { sales: number; total_orders: number; discount: number; delivery_fees: number; tax: number; net_sales: number };
  voids: { orders_qty: number; orders_value: number; items_qty: number; items_value: number };
  order_types: BizGroupRow[];
  payment_types: BizGroupRow[];
  staff: BizGroupRow[];
  categories: BizGroupRow[];
}
export interface SalesmanRow {
  salesman_id: number; name: string; name_ar: string | null;
  invoice_count: number; total_sales: number; total_returns: number;
  total_discount: number; net_total: number; services_count: number;
}
export interface SalesBySalesmanDto {
  rows: SalesmanRow[];
  totals: Omit<SalesmanRow, 'salesman_id' | 'name' | 'name_ar'>;
}
/** One modifier option chosen on an item, aggregated over the report period. */
export interface ItemDetailsModifier { group_name: string; name: string; name_ar: string | null; qty: number }
export interface ItemDetailsItemRow { item_id: number; item_name: string; item_name_ar: string | null; qty: number; amount: number; pct: number; modifiers: ItemDetailsModifier[] }
export interface ItemDetailsCategoryGroup { category_id: number; category_name: string; category_name_ar: string | null; items: ItemDetailsItemRow[]; total_qty: number; total_amount: number }
export interface ItemDetailsDto { categories: ItemDetailsCategoryGroup[]; grand_total_qty: number; grand_total_amount: number }
/**
 * One line handed back over the counter (Codes 53/54). `discount` is the
 * line's own plus its share of the header discount; `tax` its share of the
 * header VAT; `damaged_qty` what the till wrote off (Code 61) instead of
 * restocking. `entry_date` is the naive tenant wall clock.
 */
export interface ReturnedItemRow {
  receipt_id: number; receipt_no: number | null; code: number;
  entry_date: string | null; original_no: number | null;
  item_id: number; item_code: string; item_name: string; item_name_ar: string | null;
  unit: string | null; unit_ar: string | null;
  qty: number; price: number; gross: number; discount: number; net: number; tax: number; total: number;
  damaged_qty: number;
  cashier_name: string | null; cashier_name_ar: string | null;
  customer_name: string | null; customer_name_ar: string | null;
  notes: string | null;
}
export interface ReturnedItemsDto {
  rows: ReturnedItemRow[];
  totals: {
    receipts: number; lines: number; qty: number; gross: number; discount: number;
    net: number; tax: number; total: number; damaged_qty: number;
  };
  truncated: boolean;
}

/** One item + unit of the Returns report: what came back AND went back to stock (written-off returns are Waste). */
export interface ReturnsByItemRow {
  item_id: number; item_code: string; item_name: string; item_name_ar: string | null;
  unit: string | null; unit_ar: string | null;
  /** Distinct return receipts the item went back to stock on. */
  returns: number;
  /** Returned less what the till wrote off. */
  qty: number;
  /** The item's list sale price for the unit (document price when it has none). */
  sale_price: number;
  /** qty × sale_price. */
  value: number;
  gross: number; discount: number; net: number; tax: number; total: number;
  /** The returns behind the row, one per receipt, newest first. */
  documents: ReturnsByItemDocument[];
}
export interface ReturnsByItemDocument {
  receipt_id: number; receipt_no: number | null;
  /** Naive tenant wall clock. */
  entry_date: string | null;
  original_no: number | null;
  qty: number;
  cashier_name: string | null; cashier_name_ar: string | null;
  customer_name: string | null; customer_name_ar: string | null;
  notes: string | null;
}
export interface ReturnsByItemDto {
  rows: ReturnsByItemRow[];
  totals: { items: number; returns: number; qty: number; gross: number; discount: number; net: number; tax: number; total: number; value: number };
  truncated: boolean;
}
/** One item + unit of the Waste (التوالف) report: every Destruction voucher (Code 61), summed. */
export interface WasteByItemRow {
  item_id: number; item_code: string; item_name: string; item_name_ar: string | null;
  unit: string | null; unit_ar: string | null;
  /** Distinct destruction vouchers the item appears on. */
  vouchers: number;
  qty: number;
  /** Of qty, written off at the till on a customer return. */
  qty_from_returns: number;
  /** The item's list sale price for the unit (the voucher's own price when it has none). */
  sale_price: number;
  /** qty × sale_price — sale price, never cost. */
  value: number;
  /** The vouchers behind the row, newest first. */
  documents: WasteByItemDocument[];
}
export interface WasteByItemDocument {
  voucher_id: number; voucher_no: number | null;
  entry_date: string | null;
  qty: number;
  /** The customer return this was written off beside, when it was. */
  return_no: number | null;
  user_name: string | null; user_name_ar: string | null;
  notes: string | null;
}
export interface WasteByItemDto {
  rows: WasteByItemRow[];
  totals: { items: number; vouchers: number; qty: number; qty_from_returns: number; value: number };
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v != null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const reportsAPI = {
  async available(): Promise<string[]> {
    const { data } = await http().get<{ reports: string[] }>('/api/v1/reports/available');
    return data.reports;
  },
  async filters(branchId?: number): Promise<ReportFiltersDto> {
    const { data } = await http().get<ReportFiltersDto>(`/api/v1/reports/filters${qs({ branch_id: branchId })}`);
    return data;
  },
  /**
   * Every dated report takes `from_time` / `to_time` as `HH:mm`. Send both or
   * neither: with neither, the range is the whole span of days.
   */
  async quickSales(args: { branch_id: number; from: string; to: string; from_time?: string; to_time?: string; cashier_id?: number }): Promise<QuickSalesDto> {
    const { data } = await http().get<QuickSalesDto>(`/api/v1/reports/quick-sales${qs(args)}`);
    return data;
  },
  async businessSummary(args: { branch_id: number; from: string; to: string; from_time?: string; to_time?: string }): Promise<BusinessSummaryDto> {
    const { data } = await http().get<BusinessSummaryDto>(`/api/v1/reports/business-summary${qs(args)}`);
    return data;
  },
  async salesBySalesman(args: { from: string; to: string; from_time?: string; to_time?: string; salesman_id?: number }): Promise<SalesBySalesmanDto> {
    const { data } = await http().get<SalesBySalesmanDto>(`/api/v1/reports/sales-by-salesman${qs(args)}`);
    return data;
  },
  async itemDetails(args: { branch_id?: number; cashier_id?: number; from: string; to: string; from_time?: string; to_time?: string; category_ids?: string }): Promise<ItemDetailsDto> {
    const { data } = await http().get<ItemDetailsDto>(`/api/v1/reports/item-details${qs(args)}`);
    return data;
  },
  /** No permission gate; always the till's own branch (the server pins it). */
  async returnedItems(args: { cashier_id?: number; from: string; to: string; from_time?: string; to_time?: string }): Promise<ReturnedItemsDto> {
    const { data } = await http().get<ReturnedItemsDto>(`/api/v1/reports/returned-items${qs(args)}`);
    return data;
  },
  async returnsByItem(args: { cashier_id?: number; from: string; to: string; from_time?: string; to_time?: string }): Promise<ReturnsByItemDto> {
    const { data } = await http().get<ReturnsByItemDto>(`/api/v1/reports/returns/by-item${qs(args)}`);
    return data;
  },
  async wasteByItem(args: { from: string; to: string; from_time?: string; to_time?: string }): Promise<WasteByItemDto> {
    const { data } = await http().get<WasteByItemDto>(`/api/v1/reports/waste/by-item${qs(args)}`);
    return data;
  },
  async itemsStockStores(): Promise<ItemsStockStoresDto> {
    const { data } = await http().get<ItemsStockStoresDto>('/api/v1/reports/items-stock/stores');
    return data;
  },
  async itemsStock(args: { store_id?: number; from?: string; to?: string; from_time?: string; to_time?: string; q?: string; include_zero?: number }): Promise<ItemsStockDto> {
    const { data } = await http().get<ItemsStockDto>(`/api/v1/reports/items-stock${qs(args)}`);
    return data;
  },
};

// ── items stock report ───────────────────────────────────────────────────
export interface ItemsStockStoreDto { id: number; code: string; name: string; name_ar: string | null }
export interface ItemsStockStoresDto {
  stores: ItemsStockStoreDto[];
  restricted: boolean;
  default_store_id: number | null;
}
export interface ItemsStockRowDto {
  item_id: number; code: string; name: string; name_ar: string | null;
  unit: string | null; unit_ar: string | null;
  opening: number; qty_in: number; qty_out: number; closing: number;
  /** Selling price of the unit shown; optional: older APIs omit it. */
  unit_price?: number;
  /** closing × unit_price. */
  total_value?: number;
}
export interface ItemsStockTotalsDto { opening: number; qty_in: number; qty_out: number; closing: number; total_value?: number }
export interface ItemsStockStoreGroupDto {
  store_id: number; store_code: string; store_name: string; store_name_ar: string | null;
  items: ItemsStockRowDto[];
  totals: ItemsStockTotalsDto;
}
export interface ItemsStockDto {
  stores: ItemsStockStoreGroupDto[];
  totals: ItemsStockTotalsDto;
  row_count: number;
  truncated: boolean;
}

// ── stock balance report ─────────────────────────────────────────────────

export interface StockBalanceItemDto {
  id: number;
  code: string;
  name: string;
  name_ar: string | null;
  /** Item carries colour/size variants — the report adds those two columns. */
  is_configurable: boolean;
}

export interface StockBalanceRowDto {
  serial_no: number;
  store_id: number;
  store_code: string;
  store_name: string;
  store_name_ar: string | null;
  color: string;
  size: string;
  balance: number;
}

export interface StockBalanceDto {
  item: StockBalanceItemDto;
  unit_name: string | null;
  unit_name_ar: string | null;
  is_configurable: boolean;
  /** True when the user isn't restricted to a subset of warehouses. */
  all_warehouses: boolean;
  rows: StockBalanceRowDto[];
  total_balance: number;
}

export const stockBalanceAPI = {
  async searchItems(q: string, limit = 20): Promise<StockBalanceItemDto[]> {
    const { data } = await http().get<{ items: StockBalanceItemDto[] }>(
      `/api/v1/stock-balance/items${qs({ q, limit })}`,
    );
    return data.items;
  },
  async forItem(itemId: number): Promise<StockBalanceDto> {
    const { data } = await http().get<StockBalanceDto>(`/api/v1/stock-balance/items/${itemId}`);
    return data;
  },
};

// ── stock levels (negative-stock guard preview) ──────────────────────────

export interface StockCheckLineInput {
  item_id: number;
  /** Omit for the item's selling unit — the same default an order line gets. */
  unit_id?: number | null;
  /** 0 = report the level only. */
  qty: number;
}

export interface StockLevelDto {
  item_id: number;
  /** On hand at the store, in the item's inventory unit. */
  on_hand: number;
  on_hand_smallest: number;
  unit_name: string | null;
  unit_name_ar: string | null;
}

/**
 * The same balance in a requested line's OWN unit. Show THIS next to a cart
 * line: `on_hand` in `StockLevelDto` is the item's inventory unit, so a line
 * sold by the carton would be sized against pieces.
 */
export interface StockUnitLevelDto {
  item_id: number;
  unit_id: number;
  /** On hand at the store, expressed in `unit_id`. */
  on_hand: number;
  unit_name: string | null;
  unit_name_ar: string | null;
}

export interface StockShortageDto {
  item_id: number;
  code: string;
  name: string | null;
  name_ar: string | null;
  requested: number;
  available: number;
  unit_name: string | null;
  unit_name_ar: string | null;
  /** Set when the short item is a component of a kit the cashier rang. */
  via_kit_item_id: number | null;
}

export interface StockCheckDto {
  allow_negative: boolean;
  store_id: number;
  ok: boolean;
  shortages: StockShortageDto[];
  levels: StockLevelDto[];
  /** Optional so a till running ahead of the API does not crash on absence. */
  unit_levels?: StockUnitLevelDto[];
}

export const stockAPI = {
  /** Levels + shortages at a store (the cashier's branch store when omitted). */
  /**
   * `exclude_receipt_id` = the invoice being EDITED: the server adds its own
   * lines back to the shelf first (an older API ignores the field).
   */
  async check(input: { store_id?: number | null; lines: StockCheckLineInput[]; exclude_receipt_id?: number | null }): Promise<StockCheckDto> {
    const { data } = await http().post<StockCheckDto>('/api/v1/stock/check', input);
    return data;
  },
};

// ── store-to-store transfers (Code 80 request → Code 81 goods) ───────────

export type TransferStatus = 'pending' | 'received' | 'cancelled';
export type TransferScope = 'incoming' | 'outgoing' | 'all';

export interface TransferStoreDto {
  id: number;
  code: string;
  name: string;
  name_ar: string | null;
  branch_names: string[];
  is_mine: boolean;
}

export interface TransferStoresDto {
  my_store_id: number | null;
  block_negative_stock: boolean;
  stores: TransferStoreDto[];
}

export interface TransferStoreRefDto {
  id: number | null;
  name: string;
  name_ar: string | null;
}

/** Code 80 "Transfer Request" (the ask) vs Code 81 "Transfer Goods" (the move). */
export type TransferKind = 'request' | 'goods';

export interface TransferRowDto {
  id: number;
  /**
   * A `goods` row is the movement itself — the ERP writes these with no request
   * behind them, so they are always already received and never actionable.
   */
  kind: TransferKind;
  entry_no: number;
  status: TransferStatus;
  from_store: TransferStoreRefDto;
  to_store: TransferStoreRefDto;
  created_by_name: string | null;
  /** Tenant wall-clock 'YYYY-MM-DDTHH:mm:ss'. */
  created_at: string | null;
  reference_no: string | null;
  notes: string | null;
  line_count: number;
  total_qty: number;
  /** Σ line totals at transfer cost. Absent from an API older than this build. */
  total_value?: number;
  /** Σ qty × selling price of the arriving lines. */
  sale_value?: number;
  /** Goods documents: arriving lines the destination has checked in. */
  received_lines?: number;
  /** Goods document, my store is the destination, items still to check in. */
  can_receive_items?: boolean;
  /** False until the tenant has migration 063 — item check-in is then unavailable. */
  receipt_tracking?: boolean;
  received_entry_no: number | null;
  received_at: string | null;
  direction: 'incoming' | 'outgoing' | 'other';
  can_receive: boolean;
  can_cancel: boolean;
}

export interface TransferLineDto {
  id: number;
  item_id: number;
  code: string;
  name: string | null;
  name_ar: string | null;
  unit_id: number | null;
  unit_name: string | null;
  unit_name_ar: string | null;
  qty: number;
  /** Unit price at transfer cost. */
  price?: number;
  total?: number;
  /** Selling price (Sale_price1) of the line's unit. */
  sale_price?: number;
  /** qty × sale_price. */
  sale_total?: number;
  notes: string | null;
  /** On hand at the source right now, in the line's unit. Null once received. */
  available_at_source: number | null;
  /**
   * Goods documents: the destination's check-in of this line; null while it is
   * still to be received. `implied` = received in full by our till's Receive.
   */
  received?: { qty: number; by_name: string | null; at: string | null; implied: boolean } | null;
}

/** A list row with its lines (`include_lines: '1'`). */
export type TransferRowWithLinesDto = TransferRowDto & { lines?: TransferLineDto[] };

export interface TransferDetailDto extends TransferRowDto {
  lines: TransferLineDto[];
  received_receipt_id: number | null;
}

export interface CreateTransferInput {
  from_store_id: number;
  to_store_id: number;
  reference_no?: string | null;
  notes?: string | null;
  /** Receive in the same transaction; only when my store is the destination. */
  receive_now?: boolean;
  lines: Array<{ item_id: number; unit_id?: number | null; qty: number; notes?: string | null }>;
}

export const transfersAPI = {
  async stores(): Promise<TransferStoresDto> {
    const { data } = await http().get<TransferStoresDto>('/api/v1/inventory-transfers/stores');
    return data;
  },
  async pendingCount(): Promise<{ incoming: number; outgoing: number }> {
    const { data } = await http().get<{ incoming: number; outgoing: number }>(
      '/api/v1/inventory-transfers/pending-count',
    );
    return data;
  },
  async list(args: {
    /** Omitted = requests only, which is what the Transfers workspace wants. */
    kind?: TransferKind | 'all';
    scope?: TransferScope;
    status?: TransferStatus | 'all';
    q?: string;
    /** `YYYY-MM-DD`, both ends inclusive. Send both or neither. */
    from?: string;
    to?: string;
    /** `HH:mm`. Send both or neither, like every dated report. */
    from_time?: string;
    to_time?: string;
    page?: number;
    limit?: number;
    /** '1' → every row carries its lines (report print / Excel). */
    include_lines?: '0' | '1';
  }): Promise<{ data: TransferRowWithLinesDto[]; page: number; limit: number; total: number }> {
    const { data } = await http().get<{ data: TransferRowWithLinesDto[]; page: number; limit: number; total: number }>(
      `/api/v1/inventory-transfers${qs(args)}`,
    );
    return data;
  },
  async get(id: number): Promise<TransferDetailDto> {
    const { data } = await http().get<TransferDetailDto>(`/api/v1/inventory-transfers/${id}`);
    return data;
  },
  async create(input: CreateTransferInput): Promise<TransferDetailDto> {
    const { data } = await http().post<TransferDetailDto>('/api/v1/inventory-transfers', input);
    return data;
  },
  async receive(id: number): Promise<TransferDetailDto> {
    const { data } = await http().post<TransferDetailDto>(`/api/v1/inventory-transfers/${id}/receive`);
    return data;
  },
  async cancel(id: number): Promise<TransferDetailDto> {
    const { data } = await http().post<TransferDetailDto>(`/api/v1/inventory-transfers/${id}/cancel`);
    return data;
  },
  /**
   * Check in arriving items of a Code 81 goods document at my store. No stock
   * moves; already-received items are skipped.
   */
  async receiveItems(id: number, lineIds: number[]): Promise<TransferDetailDto> {
    const { data } = await http().post<TransferDetailDto>(`/api/v1/inventory-transfers/${id}/receive-items`, {
      line_ids: lineIds,
    });
    return data;
  },
};

// ── cash movements (Phase 10) ────────────────────────────────────────────

export type CashMovementType = 'drop' | 'expense' | 'pickup';

export interface CashMovementDto {
  id: number;
  pos_shift_id: number;
  movement_type: CashMovementType;
  amount: number;
  reason: string | null;
  reference_no: string | null;
  notes: string | null;
  created_by: number;
  created_by_name: string | null;
  created_at: string;
}

export interface CashMovementReasonDto {
  movement_type: CashMovementType;
  reason: string;
  label_ar: string;
  label_en: string;
}

export interface CreateCashMovementRequestDto {
  movement_type: CashMovementType;
  amount: number;
  reason?: string;
  reference_no?: string;
  notes?: string;
}

export const cashMovementsAPI = {
  async list(): Promise<CashMovementDto[]> {
    const { data } = await http().get<{ data: CashMovementDto[] }>('/api/v1/cash-movements');
    return data.data;
  },
  async create(payload: CreateCashMovementRequestDto): Promise<CashMovementDto> {
    const { data } = await http().post<CashMovementDto>('/api/v1/cash-movements', payload);
    return data;
  },
  async listReasons(): Promise<CashMovementReasonDto[]> {
    const { data } = await http().get<{ data: CashMovementReasonDto[] }>(
      '/api/v1/cash-movements/reasons',
    );
    return data.data;
  },
};

// ── Receipts management (Phase 5d) ───────────────────────────────────────
//
// list() is the primary entry point used by the Receipts sidebar tab.
// The server returns server-computed `can_edit` flags per row; the
// frontend uses them to gate the Edit button without any client-side
// permission logic.
//
// Default scope is 'current_shift' — receipts within the cashier's
// currently-open Inv_CashDrawerHistory session. Use scope='all' when
// the cashier needs to look up an old receipt for view-only access.

/** See `getInvoiceReportPdf` — an SSRS cold render outlasts the 30s default. */
const REPORT_RENDER_TIMEOUT_MS = 180_000;

export const receiptsAPI = {
  async list(query: ListReceiptsRequestQuery = {}): Promise<ListReceiptsResponse> {
    const { data } = await http().get<ListReceiptsResponse>('/api/v1/receipts', {
      params: query,
    });
    return data;
  },
  async getById(id: number): Promise<ReceiptDetail> {
    const { data } = await http().get<ReceiptDetail>(`/api/v1/receipts/${id}`);
    return data;
  },
  async patch(id: number, body: PatchReceiptRequest): Promise<ReceiptDetail> {
    const { data } = await http().patch<ReceiptDetail>(`/api/v1/receipts/${id}`, body);
    return data;
  },
  async replace(id: number, body: ReplaceReceiptRequest): Promise<CreateOrderResponse> {
    const { data } = await http().put<CreateOrderResponse>(`/api/v1/receipts/${id}`, body);
    return data;
  },
  /**
   * The A4 invoice as a PDF, rendered by the tenant's SSRS report.
   *
   * `lang` picks the report edition (`P_S_52_CashSales` / `…_ar`) and should be
   * the app's current UI language, matching what the legacy POS did with its
   * UILang cookie. The response is binary, so this asks axios for a Blob rather
   * than letting the JSON interceptor near it.
   *
   * The client's 30s default does not apply here. SSRS recycles its app domain
   * after ~10 minutes idle, so most prints hit a COLD render — measured at ~34s
   * against the live report server, against ~1.7s warm. At 30s axios aborted
   * the request four seconds short and every first print of the day surfaced as
   * a timeout. This waits past pos-api's own ceiling (`SSRS_TIMEOUT_MS`, 120s)
   * so a genuinely stuck report comes back as the server's real error rather
   * than a bare client abort; Apache's `ProxyTimeout` on the API vhost is 180s
   * and must stay at least this high.
   */
  async getInvoiceReportPdf(id: number, lang: 'ar' | 'en'): Promise<Blob> {
    const { data } = await http().get<Blob>(`/api/v1/receipts/${id}/report.pdf`, {
      params: { lang },
      responseType: 'blob',
      timeout: REPORT_RENDER_TIMEOUT_MS,
    });
    return data;
  },
  /**
   * Void (cancel) a receipt. Gated server-side on the legacy `Cancel Order`
   * permission and on the receipt still being in the caller's own OPEN shift;
   * the list's `can_void` only decides whether the button is offered.
   *
   * Idempotent: voiding an already-voided receipt comes back 200 with
   * `voided_now: false` and the original audit row, not an error — a double
   * tap on a slow link must not read as a failure at the counter.
   */
  async void(id: number, body: VoidReceiptRequest = {}): Promise<VoidReceiptResponse> {
    const { data } = await http().post<VoidReceiptResponse>(`/api/v1/receipts/${id}/void`, body);
    return data;
  },
  /**
   * The cancellation reasons this tenant classifies its voids by — the list
   * behind the void dialog's picker, straight from the ERP's own
   * `GL_OrderCancelationReasons`.
   *
   * May legitimately come back EMPTY, on a tenant that keeps no such list.
   * The caller asks for a note alone in that case rather than blocking the
   * void on a dropdown with nothing in it.
   */
  async cancelReasons(): Promise<CancelReason[]> {
    const { data } = await http().get<{ data: CancelReason[] }>(
      '/api/v1/receipts/cancel-reasons',
    );
    return data.data;
  },
  async patchPaymentMethod(
    id: number,
    body: PatchPaymentMethodRequestDto,
  ): Promise<PatchPaymentMethodResponseDto> {
    const { data } = await http().patch<PatchPaymentMethodResponseDto>(
      `/api/v1/receipts/${id}/payment-method`,
      body,
    );
    return data;
  },
};

export interface PatchPaymentMethodRequestDto {
  new_payment_type_id: number;
  /** Ignored by the server since 2026-08-31; the PIN gate was removed. */
  manager_pin?: string;
  reason?: string;
}

export interface PatchPaymentMethodResponseDto {
  receipt_id: number;
  old_payment_type_id: number;
  old_payment_label: string | null;
  new_payment_type_id: number;
  new_payment_label: string | null;
  changed_by: number;
  approved_by: number;
  approved_by_name: string | null;
  changed_at: string;
  audit_id: number;
}

// ── KDS (placeholder, feature 7 not yet implemented) ─────────────────────
export const kdsAPI = {
  async listTickets(params: { station: string; status?: KdsTicketStatus }): Promise<KdsTicket[]> {
    const { data } = await http().get<KdsTicket[]>('/api/v1/kds/tickets', { params });
    return data;
  },
  async bump(ticketId: number): Promise<KdsTicket> {
    const { data } = await http().post<KdsTicket>(`/api/v1/kds/tickets/${ticketId}/bump`);
    return data;
  },
};

// ── Re-exports ───────────────────────────────────────────────────────────
export { createApiClient, getApiErrorMessage, getApiErrorCode, getApiErrorDetails };
export type { ApiClient } from './client';

export type {
  CatalogCategory,
  CatalogItem,
  CategoryListResponse,
  ItemListResponse,
  SearchResponse,
  SyncResponse,
  LoginRequest,
  LoginResponse,
  PrintConfig,
};

// ── Delivery orders (the cashier's hand-over workspace) ───────────────────
//
// Reads RECEIPTS (Inv_Receipt_h.OrderType = 2), not cc_order, so it covers a
// delivery the till rang up as well as a phone order. The KDS "Serve" dialog
// dispatches on order type alone, and a till-created delivery order has no
// cc_order row — before this endpoint existed those orders left the kitchen
// board with nowhere for the cashier to find them and take the money.

export interface DeliveryOrderRowDto {
  id: number;
  code: number;
  entry_no: number | null;
  entry_date: string | null;
  /** Promised delivery time — same wire shape as `entry_date`; render with `formatReceiptDateTime`. */
  delivery_date?: string | null;
  reference_no: string | null;

  net_value: number;
  delivery_fees: number;
  item_count: number;

  customer_name: string | null;
  customer_name_ar: string | null;
  customer_phone: string | null;

  payment_type_id: number | null;
  payment_code: number | null;
  payment_name: string | null;
  is_paid: boolean;
  is_split: boolean;
  /** A cc_order sits on this receipt — settle it on Pending Orders, not here. */
  has_call_centre_order: boolean;

  driver_id: number | null;
  driver_name: string | null;
  driver_name_ar: string | null;

  kitchen_done: boolean;
  is_canceled: boolean;
}

export interface ListDeliveryOrdersParams {
  filter?: 'unpaid' | 'paid' | 'all';
  date_from?: string;
  date_to?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ListDeliveryOrdersResponseDto {
  data: DeliveryOrderRowDto[];
  page: number;
  limit: number;
  total: number;
}

export interface SettleDeliveryOrderRequestDto {
  payments: Array<{ payment_type_id: number; amount: number }>;
  note?: string;
}

export interface SettleDeliveryOrderResponseDto {
  receipt_id: number;
  payment_type_id: number;
  payment_label: string;
  settled_shift_id: number;
  order: DeliveryOrderRowDto;
}

/**
 * Held (parked) invoices — Inv_Receipt_h.IsHolded = 1. Holding is
 * `orderAPI.create({ ...req, hold: true })`; cancelling is `receiptsAPI.void`.
 */
/**
 * Web orders (WooCommerce, Ordable, …) that reached this till's branch in the last
 * 24 hours — the cashier's "new web order" alert polls it. Read-only.
 */
export const onlineOrdersAPI = {
  async recent(): Promise<OnlineOrderAlertsResponse> {
    const { data } = await http().get<OnlineOrderAlertsResponse>('/api/v1/online-orders/recent');
    return data;
  },
  /** One auto-print per web order across every till: true = this till prints it. */
  async claimPrint(extOrderId: number): Promise<boolean> {
    const { data } = await http().post<{ claimed: boolean }>(`/api/v1/online-orders/${extOrderId}/print-claim`, {});
    return data.claimed === true;
  },
};

export const heldOrdersAPI = {
  async list(scope: 'mine' | 'branch' = 'mine'): Promise<ListHeldOrdersResponse> {
    const { data } = await http().get<ListHeldOrdersResponse>('/api/v1/held-orders', {
      params: { scope },
    });
    return data;
  },
  /** Re-hold after the cashier changed it in the cart. Stays unpaid. */
  async update(id: number, body: UpdateHeldOrderRequest): Promise<UpdateHeldOrderResponse> {
    const { data } = await http().put<UpdateHeldOrderResponse>(`/api/v1/held-orders/${id}`, body);
    return data;
  },
  /** Is this the manager's (LoginName 'Admin') password? Throws on a wrong one. */
  async verifyManager(password: string): Promise<void> {
    await http().post('/api/v1/held-orders/verify-manager', { password });
  },
  /** Cancel a held order with the manager's password; frees its table. */
  async cancel(id: number, body: CancelHeldOrderRequest): Promise<{ receipt_id: number; entry_no: number }> {
    const { data } = await http().post<{ receipt_id: number; entry_no: number }>(
      `/api/v1/held-orders/${id}/cancel`,
      body,
    );
    return data;
  },
  /** This till has the order open (heartbeat) — other tills show it as in use. */
  async markOpen(id: number): Promise<void> {
    await http().post(`/api/v1/held-orders/${id}/open`);
  },
  /** This till has let the order go. */
  async releaseOpen(id: number): Promise<void> {
    await http().delete(`/api/v1/held-orders/${id}/open`);
  },
  /** Take the money: clears the hold, books GL, stock, shift and loyalty. */
  async pay(id: number, body: PayHeldOrderRequest): Promise<PayHeldOrderResponse> {
    const { data } = await http().post<PayHeldOrderResponse>(`/api/v1/held-orders/${id}/pay`, body);
    return data;
  },
  /** Change (free table only) or Move (an occupied table's order is merged in). */
  async moveTable(
    id: number,
    body: { branch_table_id: number; mode: 'change' | 'move' },
  ): Promise<TableActionResponse> {
    const { data } = await http().post<TableActionResponse>(`/api/v1/held-orders/${id}/table`, body);
    return data;
  },
  /** Join tables to the order, absorbing the held orders on them. */
  async mergeTables(id: number, branchTableIds: number[]): Promise<TableActionResponse> {
    const { data } = await http().post<TableActionResponse>(`/api/v1/held-orders/${id}/merge`, {
      branch_table_ids: branchTableIds,
    });
    return data;
  },
  /** Move lines (or part of a line) to another table's order, or a new one on a free table. */
  async transferItems(
    id: number,
    body: { lines: Array<{ line_id: number; qty: number }>; branch_table_id: number },
  ): Promise<TableActionResponse> {
    const { data } = await http().post<TableActionResponse>(
      `/api/v1/held-orders/${id}/transfer-items`,
      body,
    );
    return data;
  },
  /** Split lines into a new order on the same table (separate checks). */
  async separate(id: number, lines: Array<{ line_id: number; qty: number }>): Promise<TableActionResponse> {
    const { data } = await http().post<TableActionResponse>(`/api/v1/held-orders/${id}/separate`, {
      lines,
    });
    return data;
  },
  /** Every item its own order on the same table; `targets` lists the new ones. */
  async separateEach(id: number): Promise<TableActionResponse> {
    const { data } = await http().post<TableActionResponse>(`/api/v1/held-orders/${id}/separate-each`, {});
    return data;
  },
  /** Credit the held order to a waiter (Change Salesman's list and permission). */
  async assignWaiter(
    id: number,
    employeeId: number,
  ): Promise<{ order_id: number; employee_id: number; employee_name: string | null }> {
    const { data } = await http().post(`/api/v1/held-orders/${id}/waiter`, { employee_id: employeeId });
    return data;
  },
};

/** What every dine-in table action answers. */
export interface TableActionResponse {
  order_id: number;
  entry_no: number;
  /** False when every line left the order (a transfer) and it was closed. */
  still_open: boolean;
  table_ids: number[];
  /** Held orders merged into this one. */
  absorbed: Array<{ order_id: number; entry_no: number }>;
  /** Where transferred / separated lines went. */
  target: { order_id: number; entry_no: number; created: boolean } | null;
  /** Split every item: all the new orders (`target` is the first). */
  targets?: Array<{ order_id: number; entry_no: number }>;
}

export const deliveryOrdersAPI = {
  async list(params: ListDeliveryOrdersParams = {}): Promise<ListDeliveryOrdersResponseDto> {
    const { data } = await http().get<ListDeliveryOrdersResponseDto>(
      '/api/v1/delivery-orders',
      { params },
    );
    return data;
  },
  async settle(
    id: number,
    body: SettleDeliveryOrderRequestDto,
  ): Promise<SettleDeliveryOrderResponseDto> {
    const { data } = await http().post<SettleDeliveryOrderResponseDto>(
      `/api/v1/delivery-orders/${id}/settle`,
      body,
    );
    return data;
  },
};

// ── Delivery Manager (/api/v1/delivery-manager) ─────────────────────────────
// The legacy POS screen /Delivery/Manager (مدير التوصيل): the day's orders by
// delivery status, the driver/chef hand-over, and the three reports.

export interface DeliveryBoardOrderDto {
  id: number;
  code: number;
  entry_no: number | null;
  order_type: number | null;
  branch_id: number;
  /** Naive tenant wall clock + 'Z' (not UTC) — read the digits literally. */
  entry_date: string | null;
  delivery_date: string | null;
  delivered_at: string | null;
  /** mstr_DeliveryStatus: 0 New · 1 Ready · 2 Waiting for pickup · 3 On the way · 4 Delivered · 5 Returned. */
  status: number;
  driver_id: number | null;
  driver_name: string | null;
  driver_name_ar: string | null;
  chef_id: number | null;
  chef_name: string | null;
  chef_name_ar: string | null;
  customer_name: string | null;
  customer_name_ar: string | null;
  customer_phone: string | null;
  area_name: string | null;
  area_name_ar: string | null;
  block: string | null;
  address_details: string | null;
  payment_type_id: number | null;
  payment_code: number | null;
  payment_name: string | null;
  payment_name_ar: string | null;
  amount: number;
  unpaid_amount: number;
  delivery_fees: number;
  notes: string | null;
  category: string | null;
  has_call_centre_order: boolean;
  /** A held invoice — on a laundry tenant, an order taken in and paid at pickup. */
  is_held?: boolean;
  history: Array<{ status: number; at: string }>;
}

export interface DeliveryStaffDto {
  id: number;
  name: string | null;
  name_ar: string | null;
  code: string | null;
  branch_ids: number[];
}

export interface DeliveryBoardDto {
  date: string;
  branch_id: number;
  order_types: number[];
  now: string;
  orders: DeliveryBoardOrderDto[];
  branches: Array<{ id: number; name: string | null }>;
  drivers: DeliveryStaffDto[];
  chefs: DeliveryStaffDto[];
  has_categories: boolean;
  can_cash_return: boolean;
  can_credit_return: boolean;
}

export interface DeliveryBoardParams {
  date?: string;
  branch_id?: number;
  order_types?: string;
}

export interface DeliveryOrderItemDto {
  order_id: number;
  name: string | null;
  name_ar: string | null;
  qty: number;
  notes: string | null;
}

export type DeliveryAction = 'ready' | 'dispatch' | 'deliver' | 'return';

export interface DeliveryStatusChangeDto {
  action: DeliveryAction;
  expected_status: number;
  driver_id?: number | null;
  chef_id?: number | null;
  payments?: Array<{ payment_type_id: number; amount: number }>;
}

// ── Laundry (module 'laundry') ───────────────────────────────────────────

export interface LaundryBoardDto {
  orders: DeliveryBoardOrderDto[];
  drivers: DeliveryStaffDto[];
  chefs: DeliveryStaffDto[];
  counts: { received: number; ready: number; out_for_delivery: number; collected: number };
}

/**
 * The laundry counter: open orders (held invoices) + those collected today,
 * the order a scanned barcode names, Ready / Out for delivery, and Collect —
 * the held-order pay and Delivered in one transaction.
 */
export const laundryAPI = {
  async board(): Promise<LaundryBoardDto> {
    const { data } = await http().get<LaundryBoardDto>('/api/v1/laundry/orders');
    return data;
  },
  async lookup(code: string): Promise<DeliveryBoardOrderDto | null> {
    const { data } = await http().get<{ order: DeliveryBoardOrderDto | null }>('/api/v1/laundry/orders/lookup', {
      params: { code },
    });
    return data.order;
  },
  async status(
    id: number,
    body: { action: 'ready' | 'dispatch'; expected_status: number; driver_id?: number | null },
  ): Promise<{ order: DeliveryBoardOrderDto }> {
    const { data } = await http().post<{ order: DeliveryBoardOrderDto }>(`/api/v1/laundry/orders/${id}/status`, body);
    return data;
  },
  async collect(
    id: number,
    body: PayHeldOrderRequest & { expected_status: number },
  ): Promise<PayHeldOrderResponse & { delivery_status: number }> {
    const { data } = await http().post<PayHeldOrderResponse & { delivery_status: number }>(
      `/api/v1/laundry/orders/${id}/collect`,
      body,
    );
    return data;
  },
  /** Hand over a laundry order PAID at drop-off: Delivered, nothing charged. */
  async handOver(id: number, body: { expected_status: number }): Promise<{ order: DeliveryBoardOrderDto }> {
    const { data } = await http().post<{ order: DeliveryBoardOrderDto }>(`/api/v1/laundry/orders/${id}/hand-over`, body);
    return data;
  },
};

// ── Expenses — the legacy till's expense voucher (سند صرف), ported ──────────

export interface ExpenseTypeDto {
  id: number;
  code: number | null;
  name: string;
  name_ar: string | null;
  /** The legacy screen pre-filled each line with this value. */
  default_value: number;
  account_id: number;
}
export interface ExpenseTenderDto {
  id: number;
  code: number;
  name: string;
  name_ar: string | null;
  /** Cash comes out of the drawer, and off the shift's expected cash. */
  is_cash: boolean;
}
export interface ExpenseOptionsDto {
  /** False = nothing set up for this branch: the screen stays hidden. */
  available: boolean;
  types: ExpenseTypeDto[];
  tenders: ExpenseTenderDto[];
}
export interface ExpenseVoucherDto {
  id: number;
  code: string;
  date: string | null;
  created_at: string | null;
  memo: string | null;
  amount: number;
  payment_type_id: number | null;
  payment_type_name: string | null;
  payment_type_name_ar: string | null;
  is_cash: boolean;
  created_by: number | null;
  created_by_name: string | null;
  reference: string | null;
  cancelled: boolean;
  /** Only while the shift that paid it is still open. */
  cancellable?: boolean;
  lines: Array<{ type_id: number | null; type_name: string | null; type_name_ar: string | null; amount: number; note: string | null }>;
}
export interface CreateExpenseRequest {
  payment_type_id: number;
  lines: Array<{ type_id: number; amount: number; note?: string }>;
  note?: string;
}

export const expensesAPI = {
  async options(): Promise<ExpenseOptionsDto> {
    const { data } = await http().get<ExpenseOptionsDto>('/api/v1/expenses/options');
    return data;
  },
  async list(params: { from?: string; to?: string; shift?: 'current' }): Promise<{ data: ExpenseVoucherDto[]; open_shift_id: number | null }> {
    const { data } = await http().get<{ data: ExpenseVoucherDto[]; open_shift_id: number | null }>('/api/v1/expenses', { params });
    return data;
  },
  async create(body: CreateExpenseRequest): Promise<ExpenseVoucherDto> {
    const { data } = await http().post<ExpenseVoucherDto>('/api/v1/expenses', body);
    return data;
  },
  async cancel(id: number, reason: string): Promise<ExpenseVoucherDto> {
    const { data } = await http().post<ExpenseVoucherDto>(`/api/v1/expenses/${id}/cancel`, { reason });
    return data;
  },
};

export const deliveryManagerAPI = {
  async board(params: DeliveryBoardParams): Promise<DeliveryBoardDto> {
    const { data } = await http().get<DeliveryBoardDto>('/api/v1/delivery-manager/board', { params });
    return data;
  },
  async items(ids: number[]): Promise<DeliveryOrderItemDto[]> {
    if (ids.length === 0) return [];
    const { data } = await http().get<{ items: DeliveryOrderItemDto[] }>('/api/v1/delivery-manager/items', {
      params: { ids: ids.join(',') },
    });
    return data.items;
  },
  async changeStatus(
    id: number,
    body: DeliveryStatusChangeDto,
  ): Promise<{ order: DeliveryBoardOrderDto; return_receipt_id: number | null }> {
    const { data } = await http().post<{ order: DeliveryBoardOrderDto; return_receipt_id: number | null }>(
      `/api/v1/delivery-manager/orders/${id}/status`,
      body,
    );
    return data;
  },
  async linkDriver(body: {
    driver_id: number;
    chef_id?: number | null;
    order_ids: number[];
  }): Promise<{ linked: number[]; skipped: number[] }> {
    const { data } = await http().post<{ linked: number[]; skipped: number[] }>(
      '/api/v1/delivery-manager/link-driver',
      body,
    );
    return data;
  },
};
